/**
 * Onglet Calendrier de la vue d'ensemble (plan F5) : grille partagée (`CalendarGrid`) + une
 * chronologie des projets sous la grille. Lecture seule — un clic ouvre la tâche dans son
 * projet (`?vue=liste&tache=`), jamais de création ni de glisser-déposer ici.
 *
 * Chronologie en liste tabulaire (`Progress`, `role="progressbar"`) plutôt qu'en `@exiplan/gantt`
 * (repli documenté au plan §5, risque 2) : le Gantt ne porte aucune sémantique de progression
 * accessible, et son échelle de zoom n'apporte rien de plus qu'une barre positionnée sur un
 * ensemble de projets aux échelles très diverses.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarGrid } from '../../components/CalendarGrid.jsx';
import { Empty, Eyebrow, Progress } from '../../components/common.jsx';
import { buildWeekGrid } from '../../lib/calendar.js';
import { projectColor } from '../../lib/project-color.js';
import { EYEBROW } from '../../ui/theme.js';

// `assigneeId` est déjà appliqué côté serveur (§ `useOverview`, `need: 'tasks'`) : re-filtrer
// ici serait redondant, et faisait double emploi avec le filtre membre de la barre commune.
export function OverviewCalendar({ data, today, mode, anchor, onShift, locale }) {
  const { t } = useTranslation();

  const tasks = data?.tasks ?? [];
  const projects = data?.projects ?? [];

  // La grille partagée (`CalendarGrid`, facteur commun avec `PlanningView`) parle anglais
  // (`month`/`week`) ; l'URL de la vue d'ensemble parle français (`mois`/`semaine`, § contrat
  // `overview-url.js`). Sans cette traduction, le mode semaine n'atteignait jamais la grille.
  const gridMode = mode === 'semaine' ? 'week' : 'month';

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    [locale],
  );
  const dayLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
    [locale],
  );

  const [year, month] = anchor.split('-').map(Number);
  const gridTitle =
    gridMode === 'week'
      ? (() => {
          const [start, ...rest] = buildWeekGrid(anchor);
          const end = rest[rest.length - 1];
          return `${dayLabel.format(start)} → ${dayLabel.format(end)}`;
        })()
      : monthLabel.format(new Date(Date.UTC(year, month - 1, 1)));

  const itemsForDay = (iso) =>
    tasks.filter((task) => {
      if (task.kind === 'milestone') return task.dueDate === iso || task.startDate === iso;
      if (!task.startDate || !task.dueDate) return false;
      return task.startDate <= iso && iso <= task.dueDate;
    });

  const timelineProjects = projects.filter((project) => project.startDate && project.endDate);
  const unplannedProjects = projects.filter((project) => !project.startDate || !project.endDate);

  if (!data) return null;

  const isEmpty = tasks.length === 0 && projects.length === 0;

  // Bandeau de troncature déjà porté par `OverviewPage` (commun aux trois onglets) : le
  // dupliquer ici l'affichait deux fois sur l'onglet Calendrier.
  return (
    <div className="flex flex-col gap-4">
      {isEmpty ? (
        <Empty>{t('overview.empty.tasks')}</Empty>
      ) : (
        <>
          <CalendarGrid
            mode={gridMode}
            anchor={anchor}
            today={today}
            locale={locale}
            onShift={onShift}
            itemsForDay={itemsForDay}
            maxPerDay={3}
            prevLabel={t('overview.prevAria')}
            nextLabel={t('overview.nextAria')}
            title={<p className={EYEBROW}>{gridTitle}</p>}
            renderItem={(task) => {
              const color = projectColor(task.projectId);
              const done = task.status === 'done';
              return (
                <Link
                  key={task.id}
                  to={`/projets/${task.projectId}?vue=liste&tache=${task.id}`}
                  title={t('overview.chipTitle', { project: task.projectKey, task: task.title })}
                  className={`truncate border-2 px-1 py-0.5 text-[10px] ${color.border} ${color.text} ${
                    done ? 'opacity-60' : ''
                  }`}
                >
                  {task.kind === 'milestone' ? '◆ ' : ''}
                  {done && <span aria-hidden="true">✓ </span>}
                  {task.projectKey}-{task.number} · {task.title}
                  {done && ` (${t('status.done')})`}
                </Link>
              );
            }}
          />

          <div>
            <Eyebrow>{t('overview.timeline.title')}</Eyebrow>
            {timelineProjects.length === 0 && unplannedProjects.length === 0 ? (
              <Empty>{t('overview.empty.projects')}</Empty>
            ) : (
              <ul className="mt-1 flex flex-col gap-2">
                {timelineProjects.map((project) => (
                  <li key={project.id} className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-ink-faint">{project.key}</span>
                    <span className="text-sm text-ink">{project.name}</span>
                    <span className="font-mono text-[11px] text-ink-faint">
                      {project.startDate} → {project.endDate}
                    </span>
                    <div className="w-32">
                      <Progress
                        value={project.weightedProgress ?? 0}
                        label={t('projects.progressLabel', { name: project.name })}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {unplannedProjects.length > 0 && (
              <div className="mt-3">
                <Eyebrow as="p">{t('overview.timeline.unplanned')}</Eyebrow>
                <ul className="mt-1 flex flex-col gap-2">
                  {unplannedProjects.map((project) => (
                    <li key={project.id} className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-ink-faint">{project.key}</span>
                      <span className="text-sm text-ink">{project.name}</span>
                      <div className="w-32">
                        <Progress
                          value={project.weightedProgress ?? 0}
                          label={t('projects.progressLabel', { name: project.name })}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
